import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import BackButton from '../BackButton'
import { SectionItem } from '../Section'
import { getChanges } from 'components/Customize/OrgSettings/Helpers'
import { IntlProvider } from 'react-intl'
import { makeDefaultFailedContainer } from 'components/container'
import EmailCapture from './components/EmailCapture'
import ToggleSection from './components/ToggleSection'
import PageTitle from '../PageTitle'
import cx from 'classnames'
import ACCESS from 'utils/Access'
import { makeHasGate } from 'app/modules/gates/selectors'
import { List } from 'immutable'

import style from './style'

const NOTIFICATION_MESSAGE = 'Your changes have been saved'

const TOGGLE_ATTACHMENTS_HIDDEN_GATE = 'TOGGLE_ATTACHMENT_HIDDEN'

const orNull = input => input || null

class AdvancedChatWidgetSettings extends Component {
  static contextTypes = {
    app: PropTypes.object,
  }

  constructor(props) {
    super(props)

    this.onEmailFollowUpToggled = this.onEmailFollowUpToggled.bind(this)
    this.onCaptureEmailToggled = this.onCaptureEmailToggled.bind(this)
    this.onEmailCaptureMessageChanged = this.onEmailCaptureMessageChanged.bind(this)
    this.onDisableNewConversationToggled = this.onDisableNewConversationToggled.bind(this)
    this.onUpdateIncomingMsgPrompt = this.onUpdateIncomingMsgPrompt.bind(this)
    this.handleSelectUser = this.handleSelectUser.bind(this)
    this.debouncedPatchOrgSettings = _.debounce(this.patchOrgSettings.bind(this), 450)
    this.state = {
      emailCaptureSectionVisible: props.orgSettings.theme.captureEmail,
      updatedOrgSettings: this.props.orgSettings,
    }
  }

  onEmailFollowUpToggled(event) {
    const { queries } = this.context.app
    const { orgId } = this.props.embed
    const isEnabled = event.target.checked
    queries.OrgPropertiesQueries.updateProperties(
      orgId,
      { chatFallbackEnabled: isEnabled },
      NOTIFICATION_MESSAGE
    )
  }

  onCaptureEmailToggled(event) {
    const { orgSettings } = this.props
    const isEnabled = event.target.checked
    const updatedOrgSettings = orgSettings.setIn(['theme', 'captureEmail'], isEnabled)

    this.setState({ emailCaptureSectionVisible: isEnabled })
    this.patchOrgSettings(updatedOrgSettings)
  }

  onDisableNewConversationToggled(event) {
    const { orgSettings } = this.props
    const isEnabled = event.target.checked
    const updatedOrgSettings = orgSettings.setIn(['theme', 'disableNewConversations'], isEnabled)

    this.patchOrgSettings(updatedOrgSettings)
  }

  patchOrgSettings(updatedOrgSettings) {
    const { orgSettings } = this.props
    const changes = getChanges(updatedOrgSettings, orgSettings)
    const { actions } = this.context.app
    actions.DefaultEmbedActions.patchDefaultEmbed(changes, NOTIFICATION_MESSAGE)
  }

  onUpdateIncomingMsgPrompt(event) {
    const { orgSettings } = this.props
    const prompt = event.target.value
    const updatedOrgSettings = orgSettings
      .setIn(['theme', 'awayMessage'], orNull(prompt))
      .setIn(['theme', 'welcomeMessage'], orNull(prompt))

    this.debouncedPatchOrgSettings(updatedOrgSettings)
  }

  onEmailCaptureMessageChanged(message) {
    const { orgSettings } = this.props
    const updatedOrgSettings = orgSettings.setIn(['theme', 'emailCaptureMessage'], orNull(message))

    this.patchOrgSettings(updatedOrgSettings)
  }

  onThemeSettingToggled = setting => event => {
    const { orgSettings } = this.props
    const isEnabled = event.target.checked
    const updatedOrgSettings = orgSettings.setIn(['theme', setting], isEnabled)
    this.patchOrgSettings(updatedOrgSettings)
  }

  handleSelectUser = option => {
    const { orgSettings } = this.props
    const updatedOrgSettings = orgSettings.setIn(
      ['theme', 'userList'],
      option && option.value ? List([option.value]) : List()
    )
    this.setState({ updatedOrgSettings })

    this.patchOrgSettings(updatedOrgSettings, orgSettings)
  }

  render() {
    const { currentUser, orgSettings, orgProperties } = this.props
    const { emailCaptureSectionVisible } = this.state
    const theme = orgSettings.theme

    const name = currentUser.name || orgSettings.orgName
    return (
      <IntlProvider locale="en">
        <div className={style.AdvancedChatWidgetSettings}>
          <BackButton title="Drift Widget Settings" url="/settings/widget" />

          <PageTitle title="Advanced" description="Customize advanced settings like emails" />
          {!ACCESS.hasGates(TOGGLE_ATTACHMENTS_HIDDEN_GATE) && (
            <ToggleSection
              title="Allow Incoming Attachments"
              description="Let your visitors send you attachments in conversations."
              className="advanced-settings-toggle allow-attachments"
              isChecked={theme.allowEndUserFileUpload}
              onToggled={this.onThemeSettingToggled('allowEndUserFileUpload')}
            />
          )}

          {!ACCESS.hasGates(TOGGLE_ATTACHMENTS_HIDDEN_GATE) && (
            <ToggleSection
              title="Allow Outgoing Attachments"
              description="Let your team members send your visitors attachments in conversations."
              className="advanced-settings-toggle allow-attachments"
              isChecked={theme.allowAgentFileUpload}
              onToggled={this.onThemeSettingToggled('allowAgentFileUpload')}
            />
          )}

          <ToggleSection
            title="Show unread badge for first messages"
            description="When a visitor first arrives on your site, add an unread badge for new messages. Note: turning this off will likely result in decreased engagement with your Drift Widget."
            className="advanced-settings-toggle widget-badge"
            isChecked={theme.unreadBadgeEnabled}
            onToggled={this.onThemeSettingToggled('unreadBadgeEnabled')}
          />

          <ToggleSection
            title="Play Sound for New Messages"
            description="Drift will make a nice pinging noise when a site visitor gets a new chat message"
            className="advanced-settings-toggle sound-notifications"
            isChecked={theme.soundNotificationEnabled}
            onToggled={this.onThemeSettingToggled('soundNotificationEnabled')}
          />

          <ToggleSection
            title="Flash Tab for New Messages"
            description="When a visitor has a new message from your team waiting for them, flash the tab bar to grab their attention."
            className="advanced-settings-toggle flash-tab-option"
            isChecked={theme.titleNotificationEnabled}
            onToggled={this.onThemeSettingToggled('titleNotificationEnabled')}
          />

          <ToggleSection
            title="Ask Site Visitors for email"
            description="Drift can automatically ask an unidentified user for their email address when they start a conversation with you."
            className="advanced-settings-toggle ask-for-email"
            isChecked={theme.captureEmail}
            onToggled={this.onCaptureEmailToggled}
          />

          <ToggleSection
            title="Don't allow visitors to create different conversations"
            description="Visitors will still be able to respond to #DriftLink, welcome messages, and playbooks but they won't be able to start a different conversation."
            className="advanced-settings-toggle disable-new-conversations"
            isChecked={theme.disableNewConversations}
            onToggled={this.onDisableNewConversationToggled}
          />

          <EmailCapture
            name={name}
            avatarUrl={currentUser.avatarUrl}
            backgroundColor={theme.backgroundColor}
            onMessageChanged={this.onEmailCaptureMessageChanged}
            message={theme.emailCaptureMessage}
            className={cx({
              'email-capture-visible': emailCaptureSectionVisible,
              'email-capture-hidden': !emailCaptureSectionVisible,
            })}
          />

          <div className="email-followup-container">
            <ToggleSection
              title="Email Follow Up"
              description={
                <span>
                  If you send a message and it goes unread, we&rsquo;ll email it to your visitor. <br />
                  <a
                    href="https://help.drift.com/article/email-follow-up/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Learn more
                  </a>
                </span>
              }
              className="advanced-settings-toggle email-toggle-section"
              isChecked={orgProperties.chatFallbackEnabled}
              onToggled={this.onEmailFollowUpToggled}
            />
          </div>

          <div className="widget-api">
            <SectionItem
              title="Widget API"
              description="Are you a bit tech savvy? You can use our Widget API to customize the Drift widget to behave just the way you like. You can listen for Widget Events and have control Widget Configuration at runtime."
              className="widget-section-item"
            />
            <a className="widget-api-link" href="https://devdocs.drift.com/docs/widget-start" target="_blank">
              View the Widget API
            </a>
          </div>
        </div>
      </IntlProvider>
    )
  }
}

const mapStateToProps = state => ({
  hasConsolidatedWelcomeMsg: makeHasGate('has_consolidated_welcome_msg')(state),
})

const AdvancedChatWidgetSettingsContainer = makeDefaultFailedContainer(
  connect(mapStateToProps)(AdvancedChatWidgetSettings),
  {
    listenTo: [
      'stores.UserStore',
      'stores.OrgSettingsStore',
      'stores.OrgPropertiesStore',
      'stores.DefaultEmbedStore',
    ],
    fetch: {
      currentUser() {
        return this.app.stores.UserStore.getOrFetchCurrentUser()
      },
      orgSettings() {
        return this.app.stores.OrgSettingsStore.getOrgSettings()
      },
      orgProperties() {
        return this.app.stores.OrgPropertiesStore.fetchPropertiesForCurrentOrg()
      },
      embed() {
        return this.app.stores.DefaultEmbedStore.getOrFetchCurrentDefaultEmbed()
      },
    },
  }
)

export default AdvancedChatWidgetSettingsContainer



// WEBPACK FOOTER //
// ./src/scripts/components/settings/ChatWidget/components/Advanced/index.js